import DevsiteEnv from 'modules/core/DevsiteEnv';
import { PaginatedFetchData } from '../hooks/usePaginatedFetch';
import { Executor, HttpClientResponse, QueryParameters } from './Definitions';

/**
 * Returns the region subdomain infix (e.g. `.s0015`) when regional routing applies, or empty string.
 * Returns empty string when OMIT_REGION_ALIAS is true or regionAlias is absent.
 */
export const getRegionInfix = (regionAlias?: string): string =>
  regionAlias && !DevsiteEnv.OMIT_REGION_ALIAS ? `.${regionAlias}` : '';

export const getQueryParameters = (queryParams?: QueryParameters) => {
  const validParamKeys =
    queryParams &&
    Object.keys(queryParams).length &&
    Object.keys(queryParams).filter((p) => queryParams[p] || queryParams[p] === 0);

  if (!validParamKeys || validParamKeys.length === 0) {
    return '';
  }
  return `?${validParamKeys
    .map((paramKey) =>
      Array.isArray(queryParams[paramKey])
        ? queryParams[paramKey].map((val: string) => `${paramKey}=${val}`).join('&')
        : `${paramKey}=${queryParams[paramKey]}`
    )
    .join('&')}`;
};

export const getAllPaginatedData = async <T extends object>(
  executor: Executor<string>,
  pageQuery: (offset: number, length: number) => string,
  pageSize: number,
  parseData: (response: HttpClientResponse) => PaginatedFetchData<T>
): Promise<T[]> => {
  const firstPage = await executor.get(pageQuery(0, pageSize)).then(parseData);
  const totalPages = Math.ceil(firstPage.total / pageSize);
  if (totalPages <= 1) return firstPage.data;
  const remainingPages = (
    await Promise.all(
      [...Array(totalPages - 1).keys()] // start with a range
        .map((pageNumLessOne: number) =>
          executor
            .get(pageQuery((pageNumLessOne + 1) * pageSize, pageSize))
            .then(parseData)
            .then((page) => page.data)
        )
    )
  ).flat();
  return [...firstPage.data, ...remainingPages];
};

export const getBaseUrl = (fqdn: string, isWebsocket?: boolean) => {
  if (fqdn.startsWith('localhost')) return `http://${fqdn}`;
  const protocol = isWebsocket ? 'wss:' : 'https:';
  return `${protocol}//${fqdn}`;
};
